import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Search, ChevronRight, ChevronDown, ChevronUp, FileText, Users, ExternalLink, Loader2, AlertCircle, Flag, Compass, DollarSign, Briefcase, Film, HelpCircle, Lightbulb, Library, Key, CheckCircle, XCircle, Flame, FolderOpen, Sparkles, Calendar, ShieldCheck, Clock, BookOpen, Play, Link2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import SEO from '../../components/SEO';

const ExploreLegalVault = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [expandedStep, setExpandedStep] = useState('start');
  const [showArchive, setShowArchive] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);

  // Use relative URLs for API calls

  // Journey steps - matching mobile app exactly
  const journeySteps = [
    {
      id: 'start',
      number: 1,
      title: 'The Big Picture',
      subtitle: 'What you need to know first',
      color: '#E10600',
      content: 'Michael Jackson was investigated twice. Tried once. Found NOT GUILTY on ALL counts. The FBI investigated him for over 10 years. They found nothing.',
      keyPoint: 'Verdict: NOT GUILTY on all 14 counts (2005)',
    },
    {
      id: '1993',
      number: 2,
      title: '1993: The Chandler Case',
      subtitle: 'Follow the money',
      color: '#FF6B6B',
      content: 'Evan Chandler demanded $20 million BEFORE going to police. Secret recordings reveal his true motives: "If I go through with this, I win big time."',
      keyPoint: 'No criminal charges were ever filed',
      documents: ['the-settlement', 'meet-the-chandler-family-background-on-the-1993-accuser'],
    },
    {
      id: 'settlement',
      number: 3,
      title: 'The Settlement',
      subtitle: 'Why settle if innocent?',
      color: '#4ECDC4',
      content: 'The INSURANCE company settled the civil case, not Michael. Civil cases can proceed before criminal cases. Settling prevented testimony that could be used against him in a criminal trial that never happened - because the DA had no case.',
      keyPoint: 'Settlement ≠ Admission of guilt. No charges filed.',
      documents: ['the-settlement', '1993-allegations-timeline'],
    },
    {
      id: '2005',
      number: 4,
      title: '2005: The Trial',
      subtitle: 'When they finally had their day in court',
      color: '#9B59B6',
      content: "4 months. 135 witnesses. Every piece of evidence examined. The accuser's family had a history of false claims. The prosecution's case fell apart.",
      keyPoint: 'Jury verdict: NOT GUILTY on ALL 14 counts',
      documents: ['the-arvizo-family-introduction-and-background', 'arvizo-family-credibility-problems'],
    },
    {
      id: 'fbi',
      number: 5,
      title: 'The FBI Files',
      subtitle: '10+ years of investigation',
      color: '#00CED1',
      content: 'The FBI investigated Michael Jackson for over a decade. Their complete files were released under FOIA. What did they find? Nothing. Zero evidence of criminal conduct.',
      keyPoint: 'FBI files are public record - read them yourself',
      link: 'https://vault.fbi.gov/Michael%20Jackson',
    },
    {
      id: 'leaving-neverland',
      number: 6,
      title: 'Leaving Neverland',
      subtitle: 'The documentary examined',
      color: '#E74C3C',
      content: "Wade Robson testified UNDER OATH that nothing happened - multiple times. James Safechuck claimed abuse at a train station that wasn't built yet. Both filed lawsuits seeking money after Michael's death.",
      keyPoint: 'Timeline contradictions & prior sworn testimony',
      documents: ['train-station-timeline-discrepancy', 'critical-analysis-of-leaving-neverland-documentary'],
    },
  ];

  // Myth busters - matching mobile app exactly
  const mythBusters = [
    {
      myth: '"He paid off the family - guilty people pay"',
      truth: 'Insurance settled the CIVIL case. No criminal charges. Civil settlements are common and not admissions of guilt.',
    },
    {
      myth: '"They found child porn at Neverland"',
      truth: 'FALSE. Radar Online published FAKE documents in 2016. The Sheriff\'s Dept officially called them "fraudulent."',
    },
    {
      myth: '"The FBI had evidence against him"',
      truth: '10+ years of investigation. Complete files released. Result: NOTHING. Read them yourself at vault.fbi.gov.',
    },
    {
      myth: '"His own sister said he was guilty"',
      truth: 'LaToya was in an abusive relationship with Jack Gordon, who forced her to make those statements. She recanted everything after leaving him.',
    },
  ];

  // Think about this - matching mobile app exactly
  const thinkAbout = [
    {
      point: 'Why demand $20 million BEFORE going to police?',
      context: 'The Chandlers demanded money first. If your child was truly abused, would money be your first priority?',
    },
    {
      point: 'Why did the DA never file charges in 1993?',
      context: "With all the media attention and public pressure, the District Attorney still couldn't build a case.",
    },
    {
      point: 'Why did 135 witnesses and all evidence lead to acquittal?',
      context: "This wasn't a hung jury or technicality. 12 jurors saw EVERYTHING and said NOT GUILTY on every count.",
    },
    {
      point: "Why wait until he's dead to make new claims?",
      context: 'Robson & Safechuck praised Michael for years. Only after his death - and failed business ventures - did they change their stories.',
    },
  ];

  // Featured documents for archive view
  const featuredDocuments = [
    {
      slug: 'meet-the-chandler-family-background-on-the-1993-accuser',
      title: 'The Extortion Story',
      subtitle: "The Chandler family's background and their motivations",
      highlight: '"If I go through with this, I win big time. There\'s no way I lose."',
      category: 'MUST READ',
      color: '#FF6B6B'
    },
    {
      slug: 'the-settlement',
      title: 'Why The Settlement?',
      subtitle: "The real reason - and why it wasn't an admission of guilt",
      highlight: 'Civil settlement ≠ Criminal guilt. No admission. No charges filed.',
      category: 'MYTH BUSTER',
      color: '#4ECDC4'
    },
    {
      slug: '1993-allegations-timeline',
      title: 'The 1993 Timeline',
      subtitle: 'Complete chronological breakdown of events',
      highlight: 'The Chandlers demanded money first, reported to police second.',
      category: 'EVIDENCE',
      color: '#E10600'
    },
    {
      slug: 'has-child-pornography-ever-been-found-in-michael-jackson-s-possession-the-michae',
      title: 'The "Child Porn" Lie',
      subtitle: "Radar Online's fake documents exposed",
      highlight: '2005 jury saw ALL evidence. Verdict: NOT GUILTY on all counts.',
      category: 'MYTH BUSTER',
      color: '#E74C3C'
    },
    {
      slug: 'train-station-timeline-discrepancy',
      title: 'The Train Station Lie',
      subtitle: "Safechuck claims abuse at location that didn't exist",
      highlight: "Construction records prove the station wasn't built yet.",
      category: 'FACTCHECK',
      color: '#9B59B6'
    },
    {
      slug: 'critical-analysis-of-leaving-neverland-documentary',
      title: 'Leaving Neverland Debunked',
      subtitle: "Comprehensive analysis of the documentary's claims",
      highlight: 'Timeline contradictions, prior sworn testimony, and factual errors.',
      category: 'ANALYSIS',
      color: '#1E90FF'
    },
  ];

  // Category display info
  const categoryInfo = {
    '1993_allegations': { label: '1993 Case', color: '#FF6B6B' },
    '2005_allegations': { label: '2005 Trial', color: '#4ECDC4' },
    'wade_robson': { label: 'Wade Robson', color: '#9B59B6' },
    'leaving_neverland': { label: 'Leaving Neverland', color: '#E74C3C' },
    'debunking_myths': { label: 'Myth Busters', color: '#00FF00' },
    'summaries': { label: 'Summaries', color: '#E10600' },
    'faq': { label: 'FAQ', color: '#1E90FF' },
    'media_coverage': { label: 'Media', color: '#FF9500' },
    'humanity': { label: 'The Man', color: '#FF69B4' },
    'general': { label: 'General', color: '#888' },
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiFetch(`/api/legal-vault/documents`);
      if (!response.ok) throw new Error('Failed to fetch documents');
      const data = await response.json();
      const docs = data.documents || data || [];
      
      const categories = [...new Set(docs.map(d => d.category))];
      setStats({
        total_documents: docs.length,
        total_categories: categories.length,
        categories: categories.map(cat => ({
          id: cat,
          name: cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          count: docs.filter(d => d.category === cat).length
        }))
      });
    } catch (err) {
      setFetchError('Failed to load vault statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiFetch(`/api/legal-vault/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setFetchError('Failed to load categories');
    }
  }, []);

  const fetchDocumentsByCategory = useCallback(async (category) => {
    try {
      setLoading(true);
      const response = await apiFetch(`/api/legal-vault/documents?category=${encodeURIComponent(category)}`);
      if (!response.ok) throw new Error('Failed to fetch documents');
      const data = await response.json();
      setDocuments(data.documents || data || []);
    } catch (err) {
      setFetchError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCategories();
  }, [fetchStats, fetchCategories]);

  useEffect(() => {
    if (selectedCategory) {
      fetchDocumentsByCategory(selectedCategory);
    }
  }, [selectedCategory, fetchDocumentsByCategory]);

  const fetchDocument = async (docId) => {
    try {
      setLoadingDoc(true);
      setShowFullContent(false);
      const response = await apiFetch(`/api/legal-vault/documents/${docId}`);
      if (!response.ok) throw new Error('Failed to fetch document');
      const data = await response.json();
      setSelectedDocument(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setFetchError('Failed to load document');
    } finally {
      setLoadingDoc(false);
    }
  };

  const fetchDocumentBySlug = async (slug) => {
    try {
      setLoadingDoc(true);
      setShowFullContent(false);
      
      // Convert slug to search query - use only first 3-4 key words to avoid overly specific search
      const slugWords = slug.replace(/-/g, ' ').split(' ').filter(w => w.length > 2);
      const searchQuery = slugWords.slice(0, 4).join(' ');
      
      // First, search for the document by slug to get its UUID
      const searchResp = await apiFetch(`/api/legal-vault/search?q=${encodeURIComponent(searchQuery)}`);
      if (searchResp.ok) {
        const searchData = await searchResp.json();
        if (searchData.results && searchData.results.length > 0) {
          // Find exact slug match first, otherwise use first result
          const exactMatch = searchData.results.find(r => (r.document?.slug === slug) || (r.slug === slug));
          const result = exactMatch || searchData.results[0];
          const doc = result.document || result;
          
          if (doc && doc.id) {
            // Now fetch the full document by UUID
            const docResp = await apiFetch(`/api/legal-vault/documents/${doc.id}`);
            if (docResp.ok) {
              const fullDoc = await docResp.json();
              setSelectedDocument(fullDoc);
              // Scroll to top when document loads
              window.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
          }
          // If we couldn't fetch by ID, use the search result data
          setSelectedDocument(doc);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
      // Document not found for this slug
    } catch (err) {
      setFetchError('Document not found');
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    try {
      setLoading(true);
      const response = await apiFetch(`/api/legal-vault/search?q=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      setSearchResults(data);
      setShowArchive(true);
    } catch (err) {
      setFetchError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    return categoryInfo[category]?.color || '#6B7280';
  };

  const getCategoryLabel = (category) => {
    return categoryInfo[category]?.label || category?.replace(/_/g, ' ');
  };

  // Generate simple summary from content (first 2-3 sentences)
  const generateSimpleSummary = (doc) => {
    if (doc.simple_summary) return doc.simple_summary;
    if (doc.summary) return doc.summary;
    
    // Extract first few sentences from content
    const content = doc.content_text || '';
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 20);
    return sentences.slice(0, 2).join('. ').trim() + (sentences.length > 2 ? '.' : '');
  };

  // Extract key dates from content or use existing
  const getKeyDates = (doc) => {
    if (doc.timeline && doc.timeline.length > 0) return doc.timeline;
    if (doc.key_dates && doc.key_dates.length > 0) {
      // Convert string dates to timeline format
      return doc.key_dates.slice(0, 6).map(date => ({
        date: date,
        event: ''
      }));
    }
    return [];
  };

  // Get key facts from document
  const getKeyFacts = (doc) => {
    if (doc.key_facts && doc.key_facts.length > 0) return doc.key_facts;
    // Generate from key_events if available
    if (doc.key_events && doc.key_events.length > 0) {
      return doc.key_events.slice(0, 5);
    }
    return [];
  };

  // Document Detail View - MATCHING MOBILE APP DESIGN
  if (selectedDocument) {
    const simpleSummary = generateSimpleSummary(selectedDocument);
    const timeline = getKeyDates(selectedDocument);
    const keyFacts = getKeyFacts(selectedDocument);
    const wordCount = selectedDocument.content_text ? selectedDocument.content_text.split(' ').length : 0;

    return (
      <div className="min-h-screen pt-20 bg-black" data-testid="document-detail-view">
        {/* Header */}
        <section className="py-4 border-b border-gray-800" style={{ background: '#0a0a0a' }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => { setSelectedDocument(null); setShowFullContent(false); }} 
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="back-to-archive-btn"
              >
                <ArrowLeft className="w-6 h-6 text-[#E10600]" />
              </button>
              <span 
                className="px-3 py-1.5 rounded-lg text-xs font-bold text-white" 
                style={{ backgroundColor: getCategoryColor(selectedDocument.category) }}
              >
                {getCategoryLabel(selectedDocument.category)}
              </span>
              <div className="flex-1" />
              {selectedDocument.source_url && (
                <a 
                  href={selectedDocument.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="open-source-btn"
                >
                  <ExternalLink className="w-5 h-5 text-[#4ECDC4]" />
                </a>
              )}
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-6 max-w-3xl">
          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
            {selectedDocument.title}
          </h1>

          {/* IN SIMPLE TERMS Box */}
          {simpleSummary && (
            <div 
              className="mb-6 p-4 rounded-xl" 
              style={{ background: '#1A1A0A', border: '1px solid #E10600' }}
              data-testid="simple-summary-box"
            >
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-[#E10600]" />
                <span className="text-xs font-bold text-[#E10600] tracking-wider uppercase">In Simple Terms</span>
              </div>
              <p className="text-gray-200 text-sm md:text-base leading-relaxed">
                {simpleSummary}
              </p>
            </div>
          )}

          {/* Timeline Section */}
          {timeline.length > 0 && (
            <div className="mb-6" data-testid="timeline-section">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-[#4ECDC4]" />
                <h2 className="text-lg font-bold text-white">Timeline</h2>
              </div>
              <div className="ml-2">
                {timeline.map((item, index) => (
                  <div key={index} className="flex gap-3 mb-4 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-[#4ECDC4]" />
                      {index < timeline.length - 1 && (
                        <div className="w-0.5 flex-1 bg-gray-700 mt-1" />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <span className="text-sm font-semibold text-[#4ECDC4]">{item.date}</span>
                      {item.event && (
                        <p className="text-gray-300 text-sm mt-1">{item.event}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Facts Section */}
          {keyFacts.length > 0 && (
            <div className="mb-6" data-testid="key-facts-section">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-[#22C55E]" />
                <h2 className="text-lg font-bold text-white">Key Facts</h2>
              </div>
              <div className="space-y-3">
                {keyFacts.map((fact, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-[#22C55E] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-sm leading-relaxed">{fact}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key People */}
          {selectedDocument.key_people && selectedDocument.key_people.length > 0 && (
            <div className="mb-6" data-testid="key-people-section">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-[#9B59B6]" />
                <h2 className="text-lg font-bold text-white">People Mentioned</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedDocument.key_people.slice(0, 12).map((person, index) => (
                  <span 
                    key={index} 
                    className="px-3 py-1 rounded-full text-xs bg-purple-900/30 text-purple-300 border border-purple-800"
                  >
                    {person}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* External Links / Resources */}
          {selectedDocument.external_links && selectedDocument.external_links.length > 0 && (
            <div className="mb-6" data-testid="external-links-section">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-5 h-5 text-red-500" />
                <h2 className="text-lg font-bold text-white">Videos & Resources</h2>
              </div>
              <p className="text-gray-500 text-xs mb-3">Watch, listen, and read the original sources</p>
              <div className="space-y-2">
                {selectedDocument.external_links.map((link, index) => (
                  <a 
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg bg-[#1a1a1a] hover:bg-[#252525] transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-900/30">
                      <Play className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{link.title || 'View Resource'}</p>
                      <p className="text-gray-500 text-xs truncate">{link.url}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Read Full Article Button */}
          <div className="mb-6">
            <button 
              onClick={() => setShowFullContent(!showFullContent)}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold transition-colors"
              style={{ background: '#4ECDC4', color: '#000' }}
              data-testid="read-full-article-btn"
            >
              <BookOpen className="w-5 h-5" />
              <span>{showFullContent ? 'Hide Full Article' : 'Read Full Article'}</span>
              <span className="text-xs opacity-70">
                {wordCount.toLocaleString()} words
              </span>
            </button>

            {/* Full Content */}
            {showFullContent && selectedDocument.content_text && (
              <div 
                className="mt-4 p-6 rounded-xl border border-gray-800"
                style={{ background: '#0a0a0a' }}
                data-testid="full-content-section"
              >
                <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-[#1A1A0A] border border-[#E10600]/30">
                  <AlertCircle className="w-4 h-4 text-[#E10600]" />
                  <span className="text-xs text-[#E10600] italic">
                    Original source material preserved exactly as written
                  </span>
                </div>
                <div className="text-gray-300 text-sm md:text-base leading-relaxed whitespace-pre-wrap">
                  {selectedDocument.content_text}
                </div>
              </div>
            )}
          </div>

          {/* Source Attribution */}
          <div 
            className="mb-6 p-4 rounded-xl"
            style={{ background: '#1a1a1a' }}
            data-testid="source-section"
          >
            <span className="text-xs text-gray-500 uppercase tracking-wider">Source</span>
            {selectedDocument.source_url ? (
              <a 
                href={selectedDocument.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 mt-2 text-[#4ECDC4] hover:underline"
              >
                <Link2 className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  {selectedDocument.source_name || 'The Michael Jackson Allegations Blog'}
                </span>
              </a>
            ) : (
              <p className="text-sm text-[#4ECDC4] mt-2 font-semibold">
                {selectedDocument.source_name || 'The Michael Jackson Allegations Blog'}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2 italic">
              All content preserved from original source. Click to view original article.
            </p>
          </div>

          {/* Navigation Buttons */}
          <div className="space-y-3 mb-8">
            <button 
              onClick={() => { setSelectedDocument(null); setShowFullContent(false); setShowArchive(true); }}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-xl border border-gray-700 hover:bg-white/5 transition-colors"
              data-testid="browse-all-docs-btn"
            >
              <Library className="w-5 h-5 text-[#4ECDC4]" />
              <span className="text-white font-semibold">Browse All Documents</span>
            </button>
            <button 
              onClick={() => { setSelectedDocument(null); setShowFullContent(false); setShowArchive(false); }}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-xl border border-gray-700 hover:bg-white/5 transition-colors"
              data-testid="back-to-journey-btn"
            >
              <Compass className="w-5 h-5 text-[#E10600]" />
              <span className="text-white font-semibold">Back to Journey</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Archive View
  if (showArchive) {
    return (
      <div className="min-h-screen pt-20 bg-black" data-testid="archive-view">
        <section className="py-8" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
          <div className="container mx-auto px-4">
            <button 
              onClick={() => { setShowArchive(false); setSearchResults(null); setSelectedCategory(null); }} 
              className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-6"
              data-testid="back-to-journey-btn"
            >
              <ArrowLeft className="w-5 h-5" /> Back to Journey
            </button>
            
            {/* Archive Hero */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Library className="w-10 h-10 text-[#4ECDC4]" />
                <h1 className="text-3xl md:text-4xl font-bold text-white">The Truth, Documented</h1>
              </div>
              <p className="text-[#4ECDC4] mb-2">
                {stats?.total_documents || 70} articles • {((stats?.total_words || 256000) / 1000).toFixed(0)}k+ words • Court records, timelines & evidence
              </p>
              <p className="text-gray-500 text-sm italic">
                Source: The Michael Jackson Allegations Blog (used with permission)
              </p>
            </div>

            {/* Search */}
            <div className="max-w-2xl mx-auto flex gap-2 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search: 'Chandler', 'settlement', 'train station'..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()} 
                  className="w-full pl-12 pr-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-[#E10600]"
                  data-testid="archive-search-input"
                />
              </div>
              <button 
                onClick={handleSearch} 
                className="px-6 py-3 bg-[#E10600] text-black font-semibold rounded-lg hover:bg-red-400"
                data-testid="archive-search-btn"
              >
                Search
              </button>
            </div>
          </div>
        </section>

        <section className="py-8" style={{ background: '#0a0a0a' }}>
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Search Results */}
              {searchResults ? (
                <>
                  <p className="text-gray-400 mb-4">Found {searchResults.total_results} results for "{searchResults.query}"</p>
                  <div className="space-y-3">
                    {searchResults.results?.map((doc, idx) => (
                      <button 
                        key={doc.id || idx} 
                        onClick={() => fetchDocument(doc.id)} 
                        className="w-full text-left p-4 rounded-lg hover:bg-white/10 transition-colors" 
                        style={{ background: '#1a1a1a', border: '1px solid #333' }}
                        data-testid={`search-result-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <span 
                            className="px-2 py-1 rounded text-xs font-semibold"
                            style={{ background: getCategoryColor(doc.category), color: '#000' }}
                          >
                            {getCategoryLabel(doc.category)}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-semibold text-white mb-1">{doc.title}</h3>
                            <p className="text-gray-400 text-sm line-clamp-2">{doc.summary}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        </div>
                      </button>
                    ))}
                  </div>
                  <button 
                    onClick={() => { setSearchResults(null); setSearchTerm(''); }}
                    className="mt-4 text-[#4ECDC4] hover:underline"
                  >
                    Clear search
                  </button>
                </>
              ) : selectedCategory ? (
                /* Category Documents */
                <>
                  <button 
                    onClick={() => setSelectedCategory(null)} 
                    className="inline-flex items-center gap-2 text-[#E10600] hover:text-white mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to Categories
                  </button>
                  <h2 className="text-xl font-bold text-white mb-4" style={{ color: getCategoryColor(selectedCategory) }}>
                    {getCategoryLabel(selectedCategory)}
                  </h2>
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-8 h-8 text-[#E10600] animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {documents.map((doc, idx) => (
                        <button 
                          key={doc.id || idx} 
                          onClick={() => fetchDocument(doc.id)} 
                          className="w-full text-left p-4 rounded-lg hover:bg-white/10 transition-colors" 
                          style={{ background: '#1a1a1a', border: '1px solid #333' }}
                          data-testid={`category-doc-${idx}`}
                        >
                          <div className="flex items-start gap-3">
                            <FileText className="w-5 h-5 text-[#E10600] mt-1" />
                            <div className="flex-1">
                              <h3 className="font-semibold text-white mb-1">{doc.title}</h3>
                              {doc.summary && <p className="text-gray-400 text-sm line-clamp-2">{doc.summary}</p>}
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                /* Featured Documents & Categories */
                <>
                  {/* Key Evidence Section */}
                  <div className="mb-12">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-5 h-5 text-[#FF6B6B]" />
                      <h2 className="text-xl font-bold text-white">Key Evidence</h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-6">Start here to understand the truth</p>
                    
                    <div className="space-y-4">
                      {featuredDocuments.map((doc, idx) => (
                        <button
                          key={idx}
                          onClick={() => fetchDocumentBySlug(doc.slug)}
                          className="w-full text-left p-5 rounded-xl hover:scale-[1.01] transition-all"
                          style={{ background: '#1a1a1a', border: `2px solid ${doc.color}` }}
                          data-testid={`featured-doc-${idx}`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <span 
                              className="px-3 py-1 rounded-full text-xs font-bold"
                              style={{ background: doc.color, color: '#000' }}
                            >
                              {doc.category}
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-white mb-1">{doc.title}</h3>
                          <p className="text-gray-400 text-sm mb-3">{doc.subtitle}</p>
                          <div 
                            className="p-3 rounded-lg mb-3"
                            style={{ background: '#0a0a0a', borderLeft: `3px solid ${doc.color}` }}
                          >
                            <p className="text-sm italic" style={{ color: doc.color }}>"{doc.highlight}"</p>
                          </div>
                          <div className="flex items-center justify-end gap-1 text-[#4ECDC4]">
                            <span className="text-sm font-semibold">Read Full Article</span>
                            <ChevronRight className="w-4 h-4" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category Browser */}
                  <div className="mb-12">
                    <div className="flex items-center gap-2 mb-6">
                      <FolderOpen className="w-5 h-5 text-[#E10600]" />
                      <h2 className="text-xl font-bold text-white">Browse by Category</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {categories.map((cat) => (
                        <button
                          key={cat.category}
                          onClick={() => setSelectedCategory(cat.category)}
                          className="p-4 rounded-xl text-center hover:scale-[1.02] transition-all"
                          style={{ 
                            background: '#1a1a1a', 
                            border: `1px solid #333`,
                            borderTop: `3px solid ${getCategoryColor(cat.category)}`
                          }}
                          data-testid={`category-${cat.category}`}
                        >
                          <h3 className="font-semibold text-white text-sm mb-1">{cat.display_name}</h3>
                          <span className="text-xs" style={{ color: getCategoryColor(cat.category) }}>
                            {cat.document_count} docs
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Stats */}
                  {stats && (
                    <div className="p-6 rounded-xl mb-8" style={{ background: '#1a1a1a', border: '1px solid #333' }}>
                      <h3 className="text-center font-bold text-white mb-4">Archive Statistics</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-[#4ECDC4]">{stats.total_documents}</div>
                          <div className="text-xs text-gray-500">Documents</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-[#4ECDC4]">{(stats.total_words / 1000).toFixed(0)}k+</div>
                          <div className="text-xs text-gray-500">Words</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-[#4ECDC4]">{Object.keys(stats.categories || {}).length}</div>
                          <div className="text-xs text-gray-500">Categories</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-[#4ECDC4]">10+</div>
                          <div className="text-xs text-gray-500">Years FBI</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bottom CTA */}
                  <div className="p-6 rounded-xl text-center" style={{ background: '#0A1A0A', border: '2px solid #00FF00' }}>
                    <ShieldCheck className="w-10 h-10 text-[#00FF00] mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-[#00FF00] mb-2">Acquitted on ALL Counts</h3>
                    <p className="text-gray-300 text-sm">
                      In 2005, after a full trial, Michael Jackson was found NOT GUILTY on every single charge. 
                      The jury saw all the evidence. This archive helps you understand why.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Main Journey View
  return (
    <div className="min-h-screen pt-20 bg-black" data-testid="legal-vault-main">
      <SEO
        title="Michael Jackson Legal Vault — FBI Files, Court Documents, Facts | PopCrown"
        description="The most comprehensive Michael Jackson legal resource. FBI investigation files, 2005 trial documents (NOT GUILTY on all 14 counts), 1993 case timeline, myth-busting with documented evidence. Facts over fiction."
        keywords="Michael Jackson Legal Vault, MJ FBI files, Michael Jackson trial, Michael Jackson not guilty, Michael Jackson court documents, Michael Jackson innocence, MJ 2005 trial, Michael Jackson allegations debunked, Chandler case, Michael Jackson facts, Michael Jackson truth, FBI investigation Michael Jackson, vault.fbi.gov Michael Jackson"
        canonical="/explore/legal-vault"
        schema={[{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Michael Jackson Legal Vault",
          "description": "Comprehensive fact-based legal resource about Michael Jackson. FBI files, court documents, and documented evidence.",
          "url": "https://popcrownofficial.com/explore/legal-vault",
          "about": {"@type": "Person", "name": "Michael Jackson", "alternateName": "King of Pop"},
          "isPartOf": {"@type": "WebSite", "name": "PopCrown Official", "url": "https://popcrownofficial.com"},
          "speakable": {"@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"]}
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://popcrownofficial.com/"},
            {"@type": "ListItem", "position": 2, "name": "Explore", "item": "https://popcrownofficial.com/explore"},
            {"@type": "ListItem", "position": 3, "name": "Legal Vault", "item": "https://popcrownofficial.com/explore/legal-vault"}
          ]
        }]}
      />
      {/* Header */}
      <section className="py-6" style={{ background: 'linear-gradient(180deg, #1a1a1a 0%, #000 100%)' }}>
        <div className="container mx-auto px-4">
          <Link to="/explore" className="inline-flex items-center gap-2 text-[#E10600] hover:text-white transition-colors mb-4" data-testid="back-to-explore-link">
            <ArrowLeft className="w-5 h-5" /> Back to Explore
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="w-10 h-10 text-[#4ECDC4]" />
              <h1 className="text-2xl md:text-4xl font-bold text-white">Legal Vault</h1>
            </div>
            <button 
              onClick={() => setShowArchive(true)} 
              className="px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors flex items-center gap-2"
              data-testid="open-archive-btn"
            >
              <Library className="w-5 h-5" /> Archive
            </button>
          </div>
        </div>
      </section>

      {/* Hero Stats */}
      <section className="py-8" style={{ background: '#0a0a0a', borderBottom: '1px solid #222' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">The Search for Truth</h2>
          <p className="text-gray-400 mb-6">A guided journey through the documented facts</p>
          <div className="flex justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#4ECDC4]">{stats?.total_documents || 70}</div>
              <div className="text-xs text-gray-500">Documents</div>
            </div>
            <div className="w-px bg-gray-700" />
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#4ECDC4]">10+</div>
              <div className="text-xs text-gray-500">Years FBI</div>
            </div>
            <div className="w-px bg-gray-700" />
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#4ECDC4]">0</div>
              <div className="text-xs text-gray-500">Convictions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Truth Banner */}
      <section className="py-6" style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-black text-[#22C55E]">14/14</div>
                <div className="text-xs md:text-sm text-white font-semibold">CLEARED</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-[#22C55E]">ZERO</div>
                <div className="text-xs md:text-sm text-white font-semibold">CONVICTIONS</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-black text-[#22C55E]">NOTHING</div>
                <div className="text-xs md:text-sm text-white font-semibold">FBI FOUND</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Start Here Card */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: '#1A1A0A', border: '2px solid #E10600' }}>
              <Flag className="w-8 h-8 text-[#E10600] flex-shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-[#E10600]">New here?</h3>
                <p className="text-gray-300 text-sm">Follow the numbered steps below. We'll walk you through the key events and evidence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Journey */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-4">The Journey</h2>
            <div className="space-y-3">
              {journeySteps.map((step) => (
                <div 
                  key={step.id} 
                  className="rounded-xl overflow-hidden" 
                  style={{ background: '#1a1a1a', borderLeft: `4px solid ${step.color}` }}
                  data-testid={`journey-step-${step.id}`}
                >
                  <button 
                    onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)} 
                    className="w-full p-4 flex items-center gap-4 text-left hover:bg-white/5 transition-colors"
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-black font-bold text-sm" 
                      style={{ background: step.color }}
                    >
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{step.title}</h3>
                      <p className="text-sm text-gray-500">{step.subtitle}</p>
                    </div>
                    {expandedStep === step.id ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                  </button>
                  {expandedStep === step.id && (
                    <div className="px-4 pb-4 border-t border-gray-800">
                      <p className="text-gray-300 text-sm leading-relaxed mt-4 mb-4">{step.content}</p>
                      <div 
                        className="flex items-center gap-2 p-3 rounded-lg mb-4" 
                        style={{ background: `${step.color}20`, border: `1px solid ${step.color}` }}
                      >
                        <Key className="w-4 h-4" style={{ color: step.color }} />
                        <span className="text-sm font-semibold" style={{ color: step.color }}>{step.keyPoint}</span>
                      </div>
                      {step.documents && (
                        <div className="space-y-2">
                          <span className="text-xs text-gray-500">Read the evidence:</span>
                          {step.documents.map((docSlug, i) => (
                            <button 
                              key={i}
                              onClick={(e) => { e.stopPropagation(); fetchDocumentBySlug(docSlug); }} 
                              className="flex items-center gap-2 text-sm text-[#4ECDC4] hover:underline w-full"
                            >
                              <FileText className="w-4 h-4" /> View Document <ChevronRight className="w-3 h-3" />
                            </button>
                          ))}
                        </div>
                      )}
                      {step.link && (
                        <a 
                          href={step.link} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="flex items-center gap-2 text-sm text-[#E10600] hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" /> View FBI Files (Official) →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Myth Busters */}
      <section className="py-6" style={{ background: '#0a0a0a' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-1">Myth Busters</h2>
            <p className="text-gray-500 text-sm mb-4">Common claims debunked with facts</p>
            <div className="space-y-3">
              {mythBusters.map((item, index) => (
                <div key={index} className="p-4 rounded-xl" style={{ background: '#1a1a1a' }} data-testid={`myth-buster-${index}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-[#FF6B6B]" />
                    <span className="text-xs font-bold text-[#FF6B6B] tracking-wider">MYTH</span>
                  </div>
                  <p className="text-white italic mb-4">{item.myth}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-[#22C55E]" />
                    <span className="text-xs font-bold text-[#22C55E] tracking-wider">FACT</span>
                  </div>
                  <p className="text-gray-300 text-sm">{item.truth}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Think About This */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-4">Think About This...</h2>
            <div className="space-y-3">
              {thinkAbout.map((item, index) => (
                <div key={index} className="flex gap-4 p-4 rounded-xl" style={{ background: '#1a1a1a' }} data-testid={`think-about-${index}`}>
                  <Lightbulb className="w-6 h-6 text-[#E10600] flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#E10600] mb-1">{item.point}</h3>
                    <p className="text-gray-400 text-sm">{item.context}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Document Archive CTA */}
      <section className="py-6">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => setShowArchive(true)} 
              className="w-full flex items-center gap-4 p-4 rounded-xl hover:scale-[1.01] transition-all" 
              style={{ background: '#0A1A1A', border: '2px solid #4ECDC4' }}
              data-testid="archive-cta-btn"
            >
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(78, 205, 196, 0.2)' }}>
                <Library className="w-8 h-8 text-[#4ECDC4]" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-white">Full Document Archive</h3>
                <p className="text-[#4ECDC4] text-sm">{stats?.total_documents || 70} documents • Searchable • Full analysis</p>
              </div>
              <ChevronRight className="w-6 h-6 text-[#4ECDC4]" />
            </button>
          </div>
        </div>
      </section>

      {/* Sources */}
      <section className="py-6" style={{ background: '#0a0a0a' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto p-4 rounded-xl" style={{ background: '#0a0a0a', border: '1px solid #333' }}>
            <h3 className="text-sm font-bold text-gray-500 mb-3">Sources</h3>
            <p className="text-xs text-gray-600 leading-relaxed">
              • FBI Vault (vault.fbi.gov) - Official FBI releases<br/>
              • Santa Barbara Court Records - Public record<br/>
              • The MJ Allegations Blog - Used with permission<br/>
              • News archives - Contemporary reporting
            </p>
            <p className="text-xs text-gray-500 italic mt-3">We encourage you to verify everything independently.</p>
          </div>
        </div>
      </section>

      {/* Loading overlay for document fetch */}
      {loadingDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#E10600] animate-spin mx-auto mb-4" />
            <p className="text-white">Loading document...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreLegalVault;
